import { commonFAQTemplates } from "../data/faqs";
import { getFrameworkCopy } from "../data/locationContent";
import type { Location } from "../data/locations";
import type { Service } from "../data/services";

export interface LocationServiceContent {
  heroHeadline: string;
  heroSubheadline: string;
  introSection: string;
  marketAnalysis: string;
  localBenefits: string[];
  processSteps: string[];
  faqs: { q: string; a: string }[];
  testimonial: {
    quote: string;
    company: string;
    person: string;
  };
  caseStudyTeaser: {
    title: string;
    result: string;
    href: string;
  };
  ctaText: string;
  relatedLocalServices: string[];
  metaTitle: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  canonicalUrl: string;
  h1: string;
  breadcrumbs: { label: string; href: string }[];
}

function generateMetaDescription(service: Service, location: Location) {
  const copy = `${service.name} in ${location.city} for ${location.keyIndustries[0]} and growth-focused teams. Headquartered in Delhi, India. Trusted global delivery.`;
  return copy.slice(0, 158);
}

function generateH1(service: Service, location: Location) {
  return `${service.name} for High-Growth ${location.city} Businesses`;
}

function generateLocalIntro(service: Service, location: Location) {
  return `${location.city} companies need ${service.name.toLowerCase()} that can ship quickly and perform reliably under real business constraints. ${location.marketContext} OWL helps teams implement scalable systems with transparent milestones, strong quality controls, and measurable outcomes. Headquartered in Delhi, India, our delivery model supports international collaboration while keeping execution practical and business-first.`;
}

function generateMarketAnalysis(service: Service, location: Location) {
  return `${service.name} in ${location.city} is influenced by ${location.keyIndustries.join(", ")} demand, local buyer expectations, and compliance requirements such as ${location.complianceNotes.join(", ")}. OWL aligns technical architecture, delivery cadence, and reporting to this context.`;
}

function getLocalTestimonial(service: Service, location: Location) {
  return {
    quote: `"OWL helped us launch ${service.name.toLowerCase()} with clear milestones and measurable results in ${location.city}."`,
    company: `${location.city} Growth Team`,
    person: "Regional Operations Lead",
  };
}

function getLocalCaseStudy(service: Service, location: Location) {
  return {
    title: `${service.name} Transformation for a ${location.city} Business`,
    result: "Improved speed-to-market and measurable ROI within one delivery cycle.",
    href: "/case-studies",
  };
}

function getRelatedLocalServices(service: Service) {
  return service.relatedServices.slice(0, 4);
}

export function generateLocationServiceContent(
  service: Service,
  location: Location
): LocationServiceContent {
  const frameworkCopy = getFrameworkCopy(service, location);

  const faqs = [
    ...commonFAQTemplates(service.name, location.city, location.country, location.timezone),
    {
      q: `Can OWL support ${location.city} time zone overlap?`,
      a: `Yes. Our teams provide planned overlap with ${location.timezone} schedules and asynchronous project updates.`,
    },
    {
      q: `Do you support ${location.country} compliance requirements?`,
      a: `Yes. We design around relevant controls including ${location.complianceNotes.join(", ")} where applicable.`,
    },
  ];

  return {
    heroHeadline: frameworkCopy.heroHeadline,
    heroSubheadline: frameworkCopy.heroSubheadline,
    introSection: generateLocalIntro(service, location),
    marketAnalysis: generateMarketAnalysis(service, location),
    localBenefits: frameworkCopy.localBenefits,
    processSteps: frameworkCopy.processSteps,
    faqs,
    testimonial: getLocalTestimonial(service, location),
    caseStudyTeaser: getLocalCaseStudy(service, location),
    ctaText: frameworkCopy.ctaText,
    relatedLocalServices: getRelatedLocalServices(service),
    metaTitle: `${service.name} in ${location.city} | Obsidian Web Labs`,
    metaDescription: generateMetaDescription(service, location),
    ogTitle: `${service.name} Services in ${location.city}, ${location.country}`,
    ogDescription: `${service.name} delivery for ${location.city} businesses with market-aware execution and global support.`,
    canonicalUrl: `https://www.obsidianweblabs.com/services/${service.slug}/${location.slug}`,
    h1: generateH1(service, location),
    breadcrumbs: [
      { label: "Home", href: "/" },
      { label: "Services", href: "/services" },
      { label: service.name, href: `/services/${service.slug}` },
      { label: location.city, href: `/services/${service.slug}/${location.slug}` },
    ],
  };
}
